import { BaseFilterOption, NewQueryResponse } from './common.type';

import { STATION_TYPE } from '~common';

export enum VENUE_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  REVISION_REQUIRED = 'REVISION_REQUIRED',
}

// Types for API `api/admin/venue/filter-list`
export type VenueFilterListing = {
  id: string;
  locationName: string;
  addressLine1: string | null;
};

export type VenueFilterListingResponse = NewQueryResponse<VenueFilterListing>;

export type VenueStatusFilters = {
  status: VENUE_STATUS[];
};

export type VenuesRequestParams = {
  managedGroupId?: string | null;
  search?: string;
  addressLine1?: string;
  isFullNameMatch?: boolean;
  page: number;
  size: number;
  filter?: VenueStatusFilters;
};

export interface VenueFilterOption extends BaseFilterOption {
  addressLine1: string | null;
}

// Types for API `api/venue-request/list-station-at`

export type StationPlacementType = {
  id: string;
  name: string;
  externalId?: string;
  stationId?: string;
  venueId?: string;
  venueName?: string;
  placementTypeId?: string;
  placementType?: string;
  distributionChannelId?: string;
  distributionChannel?: string;
  haveAssignedStation: boolean;
  type?: STATION_TYPE;
  imei?: string;
  serialNumber?: string;
  latitude?: number;
  longitude?: number;
};

export type PendingStationPlacementResponse = {
  stationCount: number;
  nonPendingPlacements: StationPlacementType[];
  pendingPlacementsAssignedStations: StationPlacementType[];
  pendingPlacementsRemovedStations: StationPlacementType[];

  // Placements to compare
  previousNonPendingPlacements: StationPlacementType[];
};

// Venue Create Form
export type ICoordinate = {
  latitude: number;
  longitude: number;
};

export type IPlacementAttachment = {
  imei?: string;
  fullStationScreen?: string;
  stationLeftSide?: string;
  stationRightSide?: string;
  stationFrontSide?: string;
  stationPerspective?: string;
  additional?: string;
};

// enum for the status of Venue Type, Placement Type, and Distribution Channel.
export enum RECORD_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  UNKNOWN = 'UNKNOWN',
}

// API to get venue detail
export type VenueContactUser = {
  email: string;
  phone: string;
  firstName: string;
  role: string;
  lastName: string;
};

export type VenueDetail = {
  id: string;
  externalId?: string; // user-friendly id
  locationName: string;
  createdAt: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  zip?: string;
  stripeAddressId?: string;
  openingTimings?: string;
  status?: VENUE_STATUS;
  venueTypeName?: string;
  venueTypeId?: string;
  venueSubTypeId?: string;
  venueSubTypeName?: string;
  customerTypeId?: string;
  venueContact?: VenueContactUser;
  groupId?: string;
};

export type PlacementType = {
  id: string;
  name: string;
};

export type DistributionChannel = {
  id: string;
  name: string;
};
export interface OperatingHourCreateRequest {
  dayOfWeek: string;
  openTime: string;
  closeTime: string;
}

// Types for form submission
export type ICreateVenueRequestParams = {
  locationName?: string;
  venueTypeId?: string;
  venueSubTypeId?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  zip?: string;
  venueContact?: VenueContactUser;
  managedGroupId?: string | null;
  operatingHours?: OperatingHourCreateRequest[];

  // Bypass values
  openingTimings: string;
  googlePlaceId?: string;
};

export type ICreatePlacementRequestParams = {
  venueId?: string | null;
  placementTypeId?: string;
  distributionChannelId?: string;
  locationName: string;
  locationPoint?: ICoordinate;
  locationAttachments?: IPlacementAttachment;
  stationId?: string;
  sameOpeningTimingsAsVenue?: boolean;
  managedGroupId?: string | null;

  // Bypass values
  venueTypeId?: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
  addressContact?: VenueContactUser;
};

export type IDropStationRequestParams = {
  addressId: string;
  locationPoint: ICoordinate;
  locationAttachments: IPlacementAttachment;
  stationId: string;
  managedGroupId?: string | null;
};

export type ISwapStationRequestParams = {
  currentStationId: string;
  newStationId: string;
  locationAttachments: IPlacementAttachment;
  managedGroupId?: string | null;
  unpinningReasonId: string;
  unpinningNote?: string;
};

export type IRelocateStationRequestParams = {
  addressId: string;
  locationName: string;
  placementTypeId?: string;
  distributionChannelId?: string;
  location: ICoordinate;
  attachment: IPlacementAttachment;
  unpinningReasonId: string;
  unpinningNote?: string;
};

export type ICreatePinningRequestParams = {
  venueId?: string | null;
  venueRequest?: ICreateVenueRequestParams;
  addressRequests?: ICreatePlacementRequestParams[];
  dropStationRequests?: IDropStationRequestParams[];
  swapStationRequests?: ISwapStationRequestParams[];
  relocateStationRequests?: IRelocateStationRequestParams[];
};

export type PlacementTypesResponse = NewQueryResponse<PlacementType>;

export type DistributionChannelsResponse =
  NewQueryResponse<DistributionChannel>;

export interface GetVenueExistenceStatusRequest {
  googlePlaceIds: string[];
  isMobilePinning?: true;
  managedGroupId?: string;
}

export interface VenueExistenceStatus {
  googlePlaceId: string;
  isExisting: boolean;
  venueId?: string;
}

export type VenueExistenceStatusAssociateByGooglePlaceId = Map<
  string,
  VenueExistenceStatus
>;

export type GetVenueExistenceStatusResponse =
  NewQueryResponse<VenueExistenceStatus>;
